import { useState } from 'react';
import {
  Modal,
  Upload,
  Button,
  Select,
  Typography,
  message,
} from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';

import { useGetInspectionPlanByProjectQuery, useUploadKeyenceProgramsMutation, useGetKeyenceSystemsQuery } from '../../../../redux/api/quality';
import { KeyenceValidationSummary } from '../../../../interfaces/Quality.Interface';
import UploadValidationResults, { UploadValidationResult } from './UploadValidationResults';

const { Text } = Typography;

const ALLOWED_EXTENSIONS = ['.sqfx', '.sifx'];

const parseViewFromFilename = (filename: string, planName: string): string | null => {
  const cleanName = filename.replace(/\.[^/.]+$/, '');
  const parts = cleanName.split(`${planName}_`);
  if (parts.length < 2) return null;
  return parts[1]?.toUpperCase() ?? null;
};

const MultiViewUploadModal = ({
  visible,
  onClose,
  projectId,
  onUploaded,
}: {
  visible: boolean;
  onClose: () => void;
  projectId: string;
  onUploaded?: () => void;
}) => {
  const [fileEntries, setFileEntries] = useState<
    { uid: string; file: File; view?: string; parsedView?: string; deviceId?: string }[]
  >([]);
  const [results, setResults] = useState<UploadValidationResult[] | null>(null);
  const [refused, setRefused] = useState(false);

  // Removed global selectedDevice state as we now do per-file selection (or apply-to-all via UI if needed, but per-file is main goal)
  // const [selectedDevice, setSelectedDevice] = useState<number | undefined>(undefined);
  // keeping commented out or removing entirely? Removing entirely to avoid confusion.

  const { data: planData } = useGetInspectionPlanByProjectQuery(
    { projectId, samples: 'ids', project_mode: 'id' },
    { skip: !projectId }
  );

  const { data: systems } = useGetKeyenceSystemsQuery();

  const availableViews =
    planData?.views.map((s) => s.toUpperCase()) ?? [];

  const [uploadReports, { isLoading: isUploading }] =
    useUploadKeyenceProgramsMutation();

  const handleBeforeUpload = (file: File) => {
    const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      message.error(`Unsupported file type: ${extension}. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return Upload.LIST_IGNORE;
    }

    const uid = `${file.name}-${Date.now()}`;
    const parsedView = parseViewFromFilename(file.name, planData?.name || '');
    const matchedView = availableViews.includes(parsedView!) ? parsedView! : undefined;

    // Auto-detect device based on extension
    let matchedDeviceId: string | undefined;
    if (systems) {
      const matchingSystem = systems.find(s => s.file_extension === extension);
      if (matchingSystem) {
        matchedDeviceId = matchingSystem.id;
      }
    }

    setFileEntries((prev) => [
      ...prev,
      {
        uid,
        file,
        view: matchedView,
        parsedView: parsedView || undefined,
        deviceId: matchedDeviceId,
      },
    ]);

    return false;
  };


  const handleViewChange = (uid: string, selectedView: string) => {
    setFileEntries((prev) =>
      prev.map((entry) =>
        entry.uid === uid ? { ...entry, view: selectedView } : entry
      )
    );
  };

  const handleDeviceChange = (uid: string, selectedDeviceId: string) => {
    setFileEntries((prev) =>
      prev.map((entry) =>
        entry.uid === uid ? { ...entry, deviceId: selectedDeviceId } : entry
      )
    );
  };

  const handleRemove = (file: any) => {
    setFileEntries((prev) => prev.filter((entry) => entry.uid !== file.uid));
  };

  const handleUpload = async () => {
    const incomplete = fileEntries.some((entry) => !entry.view);
    if (incomplete) {
      message.warning('Please select a view for all files.');
      return;
    }

    try {
      const formData = new FormData();
      fileEntries.forEach(({ file, view, deviceId }) => {
        formData.append('files', file);
        if (view) {
          formData.append('views', view.toUpperCase());
        }
        // Append deviceId per file. If undefined, we can send empty/null or handle in backend.
        // Backend expects parallel lists.
        formData.append('keyence_device_ids', deviceId || '');
      });

      formData.set('project_id', projectId);

      // Global legacy field not needed if backend handles list.
      // formData.set('keyence_device_id', selectedDevice.toString());

      const uploaded = await uploadReports(formData).unwrap();
      const programs = Array.isArray(uploaded) ? uploaded : [];

      // The upload is kept even when the Keyence could not be reached, so say
      // which half succeeded instead of reporting a flat success.
      const notSent = programs.filter(
        (p) => p?.device_sync_status && p.device_sync_status !== 'Synced'
      );
      if (notSent.length > 0) {
        message.warning(
          `Saved to the ERP, but ${notSent.length} program(s) could not be sent to the ` +
          `Keyence yet. They will be retried automatically; you can also use ` +
          `"Send to Keyence" once the proxy is back.`,
          8
        );
      } else {
        message.success('Programs uploaded successfully');
      }

      setFileEntries([]);
      if (onUploaded) onUploaded();

      // Nothing to show when validation is off: close as before.
      if (programs.every((p) => !p.validation)) {
        onClose();
        return;
      }
      setRefused(false);
      setResults(
        programs.map((p) => ({
          key: p.id,
          fileName: p.file_name,
          view: p.view,
          deviceName: p.keyence_device_name,
          validation: p.validation ?? null,
        }))
      );
    } catch (err: any) {
      console.error(err);
      const reports: KeyenceValidationSummary[] | undefined = err?.data?.validation_reports;
      if (Array.isArray(reports) && reports.length > 0) {
        // Refused in enforcing mode: keep the file list so the operator can go
        // back, swap the bad program, and resubmit.
        setRefused(true);
        setResults(
          reports.map((r, index) => {
            const entry = fileEntries.find((e) => e.file.name === r.file_name);
            return {
              key: r.report_id ?? `${r.file_name}-${index}`,
              fileName: r.file_name ?? entry?.file.name ?? 'Program',
              view: r.view ?? entry?.view,
              deviceName: systems?.find((s) => s.id === entry?.deviceId)?.name,
              validation: r,
            };
          })
        );
        return;
      }
      message.error(err?.data?.detail || 'Failed to upload programs');
    }
  };

  const handleClose = () => {
    setFileEntries([]);
    setResults(null);
    setRefused(false);
    onClose();
  };

  const backToFiles = () => {
    setResults(null);
    setRefused(false);
  };

  if (results) {
    return (
      <Modal
        title="Keyence Program Validation"
        open={visible}
        onCancel={handleClose}
        width={900}
        centered
        footer={[
          <Button key="back" onClick={backToFiles}>
            {refused ? 'Back to files' : 'Upload more'}
          </Button>,
          <Button key="done" type="primary" onClick={handleClose}>
            {refused ? 'Close' : 'Done'}
          </Button>,
        ]}
      >
        <UploadValidationResults results={results} refused={refused} />
      </Modal>
    );
  }

  return (
    <Modal
      title="Keyence Program Uploader"
      open={visible}
      centered
      onCancel={handleClose}
      onOk={handleUpload}
      okText="Upload"
      confirmLoading={isUploading}
      okButtonProps={{ disabled: fileEntries.length === 0 }}
      width={700} // Widen modal for extra column
    >

      {/* Removed Global Device Selector */}
      {/* <div style={{ marginBottom: 16 }}> ... </div> */}

      {fileEntries.map(({ file, uid, view, parsedView, deviceId }) => (
        <div
          key={uid}
          style={{
            marginBottom: 16,
            border: '1px solid #f0f0f0',
            borderRadius: 4,
            padding: 12,
            position: 'relative',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Text strong style={{ wordBreak: 'break-all', marginRight: 8 }}>{file.name}</Text>
            <Button
              size="small"
              icon={<DeleteOutlined />}
              danger
              type="text"
              onClick={() => handleRemove({ uid })}
            />
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Select
                placeholder="Select View"
                value={view}
                style={{ width: '100%' }}
                onChange={(val) => handleViewChange(uid, val)}
                options={availableViews
                  .filter((v) => {
                    const usedByOthers = fileEntries
                      .filter((e) => e.uid !== uid)
                      .some((e) => e.view === v);
                    // Allow reuse if uploading to DIFFERENT devices?
                    // For now, let's keep view uniqueness strict per upload batch or as is.
                    // Actually, if we have Top (Device A) and Top (Device B), duplicate views SHOULD be allowed if device differs?
                    // The current logic filters out used views globally.
                    // Let's relax unique view check?
                    // Or just keep it simple: user selects view.
                    return !usedByOthers || v === view;
                  })
                  .map((v) => ({ value: v, label: v }))}
              />
              {parsedView && !view && (
                <Text type="danger" style={{ fontSize: 12 }}>
                  Parsed view "{parsedView}" not found in plan
                </Text>
              )}
            </div>

            <div style={{ flex: 1 }}>
              {(() => {
                const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
                const matchingSystems = systems?.filter(s => s.file_extension === extension) || [];

                if (matchingSystems.length === 1) {
                  const systemForExtension = matchingSystems[0];
                  return (
                    <div style={{ padding: '4px 11px', border: '1px solid #d9d9d9', borderRadius: 6, background: '#f5f5f5', color: 'rgba(0, 0, 0, 0.25)' }}>
                      {systemForExtension.name}
                    </div>
                  );
                }

                const options = matchingSystems.length > 0
                  ? matchingSystems.map((s) => ({ value: s.id, label: s.name }))
                  : systems?.map((s) => ({ value: s.id, label: s.name })) || [];

                return (
                  <Select
                    placeholder="Select Device"
                    value={deviceId}
                    allowClear
                    style={{ width: '100%' }}
                    onChange={(val) => handleDeviceChange(uid, val)}
                    options={options}
                  />
                );
              })()}
            </div>
          </div>
        </div>
      ))}


      <Upload.Dragger
        multiple
        showUploadList={false}
        beforeUpload={handleBeforeUpload}
        onRemove={handleRemove}
        fileList={fileEntries.map((entry) => entry.file as unknown as UploadFile)}
        customRequest={({ onSuccess }) => {
          // Prevent default upload behavior
          setTimeout(() => {
            onSuccess && onSuccess("ok");
          }, 0);
        }}
      >
        <p className="ant-upload-drag-icon">
          <UploadOutlined />
        </p>
        <p className="ant-upload-text">Click or drag files to this area to upload</p>
        <p className="ant-upload-hint">
          Supports multiple <strong>.sqfx</strong> and <strong>.sifx</strong> files.
        </p>
      </Upload.Dragger>
    </Modal>
  );
};

export default MultiViewUploadModal;
